import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { getTeamPerformance, getTeamMembers, getCleaningCompanies } from '../lib/api';
import { getUser } from '../lib/auth';
import MobileNav from '../components/MobileNav';
import StaffFilterSelect from '../components/StaffFilterSelect';
import { ArrowLeft, Clock, Trophy, Users, BarChart3, Zap, ChevronRight, SprayCan } from 'lucide-react';

const PRESETS = [
  { key: '1w', label: '1 week', days: 7 },
  { key: '2w', label: '2 weeks', days: 14 },
  { key: '1m', label: 'Last month', days: 30 },
  { key: 'custom', label: 'Custom', days: null },
];

const fmt = (mins) => {
  if (mins === null || mins === undefined) return '--';
  const m = Math.round(mins);
  const h = Math.floor(m / 60);
  const r = m % 60;
  if (h && r) return `${h}h ${r}m`;
  if (h) return `${h}h`;
  return `${r}m`;
};

const iso = (d) => d.toISOString().split('T')[0];

const RANK_STYLES = {
  1: { ring: 'ring-2 ring-amber-300', badge: 'bg-amber-400 text-white', label: '🥇' },
  2: { ring: 'ring-1 ring-slate-300', badge: 'bg-slate-300 text-slate-700', label: '🥈' },
  3: { ring: 'ring-1 ring-orange-200', badge: 'bg-orange-300 text-white', label: '🥉' },
};

export default function TeamPerformancePage() {
  const navigate = useNavigate();
  const isAdmin = getUser()?.role === 'admin';

  const [preset, setPreset] = useState('1w');
  const [customStart, setCustomStart] = useState(iso(new Date(Date.now() - 7 * 864e5)));
  const [customEnd, setCustomEnd] = useState(iso(new Date()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [staffFilter, setStaffFilter] = useState('all');
  const [staffList, setStaffList] = useState([]);
  const [cleaningGroups, setCleaningGroups] = useState([]);

  useEffect(() => {
    getTeamMembers()
      .then((res) => setStaffList((res.data.members || []).filter((m) => m.role === 'staff')))
      .catch(() => {});
    getCleaningCompanies()
      .then((res) => {
        const groups = (res.data.companies || [])
          .map((c) => ({ id: c.id, name: c.name, cleaners: c.cleaners || [] }))
          .filter((g) => g.cleaners.length > 0);
        setCleaningGroups(groups);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!isAdmin) { navigate('/team'); return; }
    load();
    // eslint-disable-next-line
  }, [preset, customStart, customEnd]);

  const load = async () => {
    setLoading(true);
    try {
      let start, end;
      if (preset === 'custom') {
        start = customStart; end = customEnd;
      } else {
        const days = PRESETS.find((p) => p.key === preset).days;
        end = iso(new Date());
        start = iso(new Date(Date.now() - days * 864e5));
      }
      const res = await getTeamPerformance(start, end);
      setData(res.data);
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const matchFilter = (r) => {
    if (staffFilter === 'all') return true;
    if (staffFilter === 'team:all') return !r.cleaningCompanyId;
    if (staffFilter === 'cc:all') return !!r.cleaningCompanyId;
    if (staffFilter.startsWith('cc:')) return r.cleaningCompanyId === staffFilter.slice(3);
    return r.id === staffFilter;
  };
  const ranked = (data?.ranked || []).filter(matchFilter);
  const noData = (data?.no_data || []).filter(matchFilter);
  const slowest = ranked.length ? Math.max(...ranked.map((r) => r.avg_minutes)) : 0;

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-500 to-emerald-500 text-white">
        <div className="max-w-4xl mx-auto px-4 pt-6 pb-8">
          <button onClick={() => navigate('/team')} className="flex items-center gap-1.5 text-white/90 hover:text-white text-sm font-medium mb-4" data-testid="team-perf-back">
            <ArrowLeft size={18} /> Back to Team
          </button>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-white/20 flex items-center justify-center">
              <Trophy size={24} />
            </div>
            <div>
              <h1 className="text-2xl font-extrabold leading-tight" data-testid="team-perf-title">Team Performance</h1>
              <p className="text-white/85 text-sm">Average cleaning time leaderboard</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 -mt-4">
        {/* Staff / company filter */}
        {(staffList.length > 0 || cleaningGroups.length > 0) && (
          <div className="mb-4" data-testid="team-perf-filter">
            <StaffFilterSelect value={staffFilter} onChange={setStaffFilter} staffList={staffList} cleaningGroups={cleaningGroups} />
          </div>
        )}
        {/* Period selector */}
        <div className="flex flex-wrap items-center gap-2 mb-5" data-testid="team-perf-period">
          {PRESETS.map((p) => (
            <button
              key={p.key}
              onClick={() => setPreset(p.key)}
              className={`px-4 h-10 rounded-xl text-sm font-semibold border-2 transition-colors ${
                preset === p.key ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
              }`}
              data-testid={`team-period-${p.key}`}
            >
              {p.label}
            </button>
          ))}
        </div>

        {preset === 'custom' && (
          <div className="flex flex-wrap items-end gap-3 mb-5 bg-white rounded-2xl p-4 shadow-sm border border-slate-100">
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">From</label>
              <input type="date" value={customStart} max={customEnd} onChange={(e) => setCustomStart(e.target.value)} className="input-3d h-11 px-3" data-testid="team-custom-start" />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase mb-1">To</label>
              <input type="date" value={customEnd} min={customStart} max={iso(new Date())} onChange={(e) => setCustomEnd(e.target.value)} className="input-3d h-11 px-3" data-testid="team-custom-end" />
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-center py-16 text-slate-400" data-testid="team-perf-loading">Loading...</div>
        ) : !ranked.length ? (
          <div className="bg-white rounded-2xl p-10 shadow-sm border border-slate-100 text-center" data-testid="team-perf-empty">
            <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
              <BarChart3 size={30} className="text-slate-300" />
            </div>
            <h3 className="text-lg font-bold text-slate-800">No cleaning data yet</h3>
            <p className="text-sm text-slate-500 mt-1 max-w-sm mx-auto">
              Once your staff start marking properties as ready, their average cleaning times will appear here as a leaderboard.
            </p>
          </div>
        ) : (
          <>
            {/* Summary cards */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100" data-testid="team-stat-avg">
                <div className="flex items-center gap-2 text-slate-400 mb-2"><Clock size={16} /><span className="text-xs font-semibold uppercase tracking-wide">Team average</span></div>
                <div className="text-3xl font-extrabold text-slate-900">{fmt(data.team_avg_minutes)}</div>
              </motion.div>
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100" data-testid="team-stat-total">
                <div className="flex items-center gap-2 text-slate-400 mb-2"><Users size={16} /><span className="text-xs font-semibold uppercase tracking-wide">Cleanings</span></div>
                <div className="text-3xl font-extrabold text-slate-900">{data.total_cleanings}</div>
                <p className="text-xs text-slate-400 mt-2">{ranked.length} staff ranked</p>
              </motion.div>
            </div>

            {/* Leaderboard */}
            <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wide mb-2 ml-1">Leaderboard · fastest first</h3>
            <div className="space-y-2.5" data-testid="team-leaderboard">
              {ranked.map((r, i) => {
                const style = RANK_STYLES[r.rank];
                const pct = slowest ? Math.max(12, (r.avg_minutes / slowest) * 100) : 100;
                return (
                  <motion.button
                    key={r.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.04 }}
                    onClick={() => navigate(`/team/performance/${r.id}`)}
                    className={`w-full text-left bg-white rounded-2xl p-4 shadow-sm border border-slate-100 hover:shadow-md transition-all active:scale-[0.99] ${style ? style.ring : ''}`}
                    data-testid={`leaderboard-row-${r.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-xl flex items-center justify-center text-sm font-extrabold flex-shrink-0 ${style ? style.badge : 'bg-slate-100 text-slate-500'}`}>
                        {style ? style.label : r.rank}
                      </div>
                      {r.photoUrl ? (
                        <img src={r.photoUrl} alt={r.name} className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
                      ) : (
                        <div className={`w-10 h-10 rounded-full text-white flex items-center justify-center font-bold flex-shrink-0 ${r.cleaningCompanyId ? 'bg-gradient-to-br from-purple-500 to-indigo-500' : 'bg-gradient-to-br from-blue-400 to-emerald-400'}`}>
                          {r.name.charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="font-bold text-slate-900 truncate flex items-center gap-1.5">
                          <span className="truncate">{r.name}</span>
                          {r.cleaningCompanyId && (
                            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-purple-50 text-purple-600 border border-purple-200 text-[10px] font-bold flex-shrink-0" title={r.cleaningCompanyName || 'Cleaning company'}>
                              <SprayCan size={9} /> {r.cleaningCompanyName || 'Cleaning co.'}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <div className="h-2 rounded-full bg-slate-100 flex-1 overflow-hidden max-w-[140px]">
                            <div className={`h-full rounded-full bg-gradient-to-r ${r.cleaningCompanyId ? 'from-purple-500 to-indigo-500' : 'from-blue-500 to-emerald-500'}`} style={{ width: `${pct}%` }} />
                          </div>
                          <span className="text-[11px] text-slate-400 flex-shrink-0">{r.cleanings} cleaning{r.cleanings !== 1 ? 's' : ''}</span>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="text-lg font-extrabold text-slate-900">{fmt(r.avg_minutes)}</div>
                        <div className="flex items-center justify-end gap-0.5 text-[11px] text-emerald-600 font-semibold">
                          <Zap size={11} /> best {fmt(r.fastest_minutes)}
                        </div>
                      </div>
                      <ChevronRight size={18} className="text-slate-300 flex-shrink-0" />
                    </div>
                  </motion.button>
                );
              })}
            </div>

            {noData.length > 0 && (
              <>
                <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wide mb-2 mt-5 ml-1">No data in this period</h3>
                <div className="flex flex-wrap gap-2" data-testid="team-no-data">
                  {noData.map((r) => (
                    <span key={r.id} className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full border text-sm ${r.cleaningCompanyId ? 'bg-purple-50 border-purple-200 text-purple-600' : 'bg-white border-slate-200 text-slate-500'}`}>
                      {r.cleaningCompanyId && <SprayCan size={11} />}{r.name}
                    </span>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </div>

      <MobileNav />
    </div>
  );
}
